import styled from "styled-components";
import CalendarOnlyPicker from "./CustomCalendar";
import { Box } from "@mui/material";
import {
  ApplyButton,
  CalendarFooterWrapper,
  ResetButton,
} from "./CalendarStyled";
import { CalendarFooter } from "./CalendarFooter";
import PropTypes from "prop-types";
import { handleRangeChange, handleSingleChange, handleApply } from "./CalenderUtils";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  font-weight: 600;
  font-family: 'Manrope', sans-serif;
  font-size: 14px;
  padding: 16px;
  border-bottom: 1px solid #f3f4f7;
`;

const Content = styled.div`
  display: flex;
  flex-direction: row;
`;

const Sidebar = styled.div`
  width: 160px;
  border-right: 1px solid #eee;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SidebarItem = styled.div`
  font-family: 'Manrope', sans-serif;
  font-size: 13px;
  color: ${({ selected }) => (selected ? "#0C53BB" : "#48566b")};
  font-weight: 500;
  padding: 8px 8px;
  margin-left: 8px;
  border-radius: 6px;
  background: ${({ selected }) => (selected ? "#F1F6FE" : "transparent")};
  cursor: pointer;
  &:hover {
    background: #f5f5f5;
  }
`;

const CalendarSection = styled.div`
  display: flex;
  flex-direction: row;
  // height: 310px;

  .ant-picker {
    border: none !important;
    box-shadow: none !important;
    padding: 0 !important;
    background: none !important;
  }

  .ant-picker-input {
    display: none !important;
  }
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  border-top: 1px solid #f3f4f7;
  padding: 16px;
`;

const TIME_FILTER_OPTIONS = [
  { label: "Today", value: "today" },
  { label: "Yesterday", value: "yesterday" },
  { label: "Last 7 days", value: "last_7_days" },
  { label: "Last 14 days", value: "last_14_days" },
  { label: "Custom date", value: "custom_date" },
  { label: "Custom date range", value: "date_range" },
];

const CUSTOM_FILTERS = new Set(["custom_date", "date_range"]);

export const CustomDateRangePopover = ({
  onApply,
  onClose,
  selectedCustomFilter,
  customDate,
  setCustomDate,
  customRange,
  setCustomRange,
  appliedRange,
  setApply,
  handleSelectSidemenu,
}) => {
  const PREDEFINED_KEYS = ["today", "yesterday", "last_7_days", "last_14_days"];
  const isPredefined = PREDEFINED_KEYS.includes(selectedCustomFilter);
  const isDualCalendar =
    selectedCustomFilter !== "custom_date" &&
    (selectedCustomFilter === "date_range" ||
      (appliedRange.startDate &&
        appliedRange.endDate &&
        !appliedRange.startDate.isSame(appliedRange.endDate, "month")));

  const isApplyDisabled =
    (selectedCustomFilter === "date_range" &&
      (!customRange.startDate || !customRange.endDate)) ||
    (selectedCustomFilter === "custom_date" && !customDate);

    const footerStyle = {
      justifyContent: CUSTOM_FILTERS.has(selectedCustomFilter) ? "space-between" : "flex-end",
    };

  return (
    <Wrapper>
      <Header>Custom Calendar</Header>
      <Content>
        <Sidebar>
          {TIME_FILTER_OPTIONS.map((item) => (
            <SidebarItem
              key={item.value}
              onClick={() => handleSelectSidemenu(item.value)}
              selected={selectedCustomFilter === item.value}
              data-testid={item.value}
            >
              {item.label}
            </SidebarItem>
          ))}
        </Sidebar>
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <CalendarSection>
            <CalendarOnlyPicker
              dual={isDualCalendar}
              startDate={
                isPredefined ? appliedRange?.startDate : customRange?.startDate
              }
              endDate={
                isPredefined ? appliedRange?.endDate : customRange?.endDate
              }
              selectedDate={isPredefined ? appliedRange?.startDate : customDate}
              type={selectedCustomFilter}
              onChange={
                selectedCustomFilter === "date_range"
                  ? (date) => handleRangeChange(date, customRange, setCustomRange)
                  : (date) => handleSingleChange(date, setCustomDate)
              }
            />
          </CalendarSection>
          {CUSTOM_FILTERS.has(selectedCustomFilter) && (
            <CalendarFooterWrapper>
              <CalendarFooter
                text={isDualCalendar ? "Start Date" : "Selected date"}
                date={isDualCalendar ? customRange.startDate : customDate}
              />
              {isDualCalendar && (
                <CalendarFooter text="End Date" date={customRange.endDate} />
              )}
            </CalendarFooterWrapper>
          )}
        </Box>
      </Content>
      <Footer style={footerStyle}>
        {CUSTOM_FILTERS.has(selectedCustomFilter) && (
          <ResetButton
            onClick={() =>
              selectedCustomFilter === "date_range"
                ? setCustomRange({ startDate: null, endDate: null })
                : setCustomDate(null)
            }
          >
            Reset
          </ResetButton>
        )}
        <ApplyButton onClick={() => handleApply(onApply, setApply, onClose)} disabled={isApplyDisabled}>
          Apply
        </ApplyButton>
      </Footer>
    </Wrapper>
  );
};

CustomDateRangePopover.propTypes = {
  onApply: PropTypes.func,
  onClose: PropTypes.func,
  selectedCustomFilter: PropTypes.string,
  customDate: PropTypes.shape({
    startDate: PropTypes.object,
    endDate: PropTypes.object,
  }),
  setCustomDate: PropTypes.func,
  customRange: PropTypes.shape({
    startDate: PropTypes.object,
    endDate: PropTypes.object,
  }),
  setCustomRange: PropTypes.func,
  appliedRange: PropTypes.shape({
    startDate: PropTypes.object,
    endDate: PropTypes.object,
  }),
  setApply: PropTypes.func,
  handleSelectSidemenu: PropTypes.func,
};
